/**
 * TOTP MFA enrolment adapter (M02 §16 / M02-G01).
 *
 * Wires the admin UI to the real backend self-service endpoints:
 *   POST /v1/mfa/totp/setup    → server-issued secret + otpauth:// URI
 *   POST /v1/mfa/totp/confirm  → validates the first 6-digit code, activates
 *
 * The Vite dev proxy rewrites /api → /v1, so the UI calls /mfa/* through the
 * shared api client (which auto-attaches the in-memory access token). No
 * secret is ever generated or hardcoded on the client — it is issued per user
 * by MfaService.setupTotp and stored server-side in t026_mfa_configuration.
 */

import { apiRequest } from '../api/client.js';

export interface TotpSetup {
  /** Base32 secret for manual entry into the authenticator app. */
  readonly secret: string;
  /** otpauth://totp/... provisioning URI encoded into the QR code. */
  readonly otpauthUri: string;
  /** Issuer label shown in the authenticator app (e.g. "TradeLogic"). */
  readonly issuer: string;
}

interface SetupWire {
  readonly success?: boolean;
  readonly secret: string;
  readonly otpauthUri: string;
  readonly issuer: string;
}

/** Begin TOTP enrolment for the current (authenticated) user. */
export async function setupTotp(): Promise<TotpSetup> {
  const res = await apiRequest<SetupWire>('/mfa/totp/setup', {
    method: 'POST',
    body: {},
  });
  return { secret: res.secret, otpauthUri: res.otpauthUri, issuer: res.issuer };
}

/** Confirm enrolment by validating the first 6-digit authenticator code. */
export async function confirmTotp(totpCode: string): Promise<void> {
  await apiRequest<{ success?: boolean; enabled?: boolean }>('/mfa/totp/confirm', {
    method: 'POST',
    body: { totpCode },
  });
}
